import React, { useEffect, useState } from 'react';
import PaymentSummaryDetails from './components/PaymentSummaryDetails/PaymentSummaryDetails';
import PaymentFailedPopup from 'components/PaymentFailedPopup/PaymentFailedPopup';
import styles from './PaymentPlanSummaryPageData.module.scss';
import PageLink from 'components/PageLink/PageLink';
import { useHistory } from 'react-router-dom';
import { fetcher } from 'app/http';
import qs from 'qs';
import { useAppDispatch } from 'app/hooks';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import { getGTMVariables, getGTMVariablesPageView } from 'common/helpers/Gtm';
import TagManager from 'react-gtm-module';
import classNames from 'classnames/bind';
import SurveyResultPopup from 'components/SurveyResultPopup/SurveyResultPopup';

interface Props {
  PageData?: any;
  Model?: any;
  LayoutModel?: any;
}

const PaymentPlanSummaryPageData = (props: Props) => {
  const { Model, PageData, LayoutModel } = props;
  const history = useHistory();
  const [IsSelfPayer, setIsSelfPayer] = useState<boolean>(
    Model.IsManualPaymentMethod,
  );
  const [eventTracked, setEventTracked] = useState<boolean>(false);
  const [settlementPlanSetupEventTracked, setSettlementPlanSetupEventTracked] =
    useState<boolean>(false);
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState(
    Model.IsPaymentError || Model.IsPaymentTokenExpired,
  );
  const [showPaymentSecondAttempt, setShowPaymentSecondAttempt] = useState(
    Model.IsPaymentSecondAttempt,
  );
  const [showSurveyPopup, setShowSurveyPopup] = useState(
    !Model.IsPaymentError && Model.IsRatingSectionVisible && LayoutModel.EnableNetigateSurvey
  );

  const getPathnameFromUrl = (url: string) => {
    const link = new URL(url);
    return link.pathname + link.search;
  };
  const onAutoRetryClick = async () => {
    try {
      const response = await fetcher.post(Model.AutoRetryPlanCreationUrl);
      // Get pathname from enpoint url, pathname will be used for redirect and as a key in content state

      if (response && response.data?.status === 'redirect') {
        const redirectPath = getPathnameFromUrl(response.data.data);
        history.push(redirectPath);
      }
    } catch (err) {
      console.error(err);
    }
  };
  if (Model.IsAutoRetryPlanCreation) {
    onAutoRetryClick();
  }
  
  useEffect(() => {
    var tagManagerArgs: any;
    if (!Model.IsPaymentError && !Model.IsPaymentTokenExpired) {
      var revenueAmount = ((Model.RegularPaymentValue || 0) / 100).toFixed(2);
      var lastPaymentValue = ((Model.LastPaymentValue || 0) / 100).toFixed(2);
      tagManagerArgs = {
        dataLayer: {
          event: 'purchase',
          payment_type: 'Payment Plan Created',
          payment_method: Model?.PaymentMethodNameRaw,
          checkout_name: 'Payment Plan Creation',
          payment_frequency: 'Monthly',
          installment_count: Model?.NumberOfRegularPayments,
          final_installment_amount: lastPaymentValue,
          agreed_rate_amount: revenueAmount,
          is_self_payer: IsSelfPayer,
          is_secured_user: LayoutModel?.IsLoggedInUserSecured,
          ...(LayoutModel?.IsLoggedInUserSecured && {secured_case_status : LayoutModel?.SecuredCaseStatus} ),
          ecommerce: {
            transaction_id: Model?.ExternalPaymentReference,
            affiliation: 'Hoist Finance',
            value: IsSelfPayer ? '0' : '1.00',
            currency: Model?.Currency,
            items: [
              {
                item_id: 'Payment Plan Created',
                item_name: 'Payment Plan Created',
                price: IsSelfPayer ? '0' : '1.00',
                quantity: '1',
              },
            ],
          },
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    } else {
      tagManagerArgs = {
        dataLayer: {
          event: 'form_error',
          checkout_name: 'Payment Plan Creation',
          error_message: 'Failed payment',
          failure_number: Model.IsPaymentSecondAttempt ? '2' : '1',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
    setShowPaymentFailedPopup(
      Model.IsPaymentError || Model.IsPaymentTokenExpired,
    );
    setShowPaymentSecondAttempt(Model.IsPaymentSecondAttempt);
  }, [Model]);

  useEffect(() => {
    if (LayoutModel != undefined) {
      var userid =
        LayoutModel?.Profiles.length > 0
          ? LayoutModel?.Profiles[0].DebtReference
          : '';
      var isLoggedIn = LayoutModel?.HasProfile;
      var lang = PageData?.Language;
      var env = LayoutModel?.Environment;
      var reg = LayoutModel?.CountryCode;
      getGTMVariables(userid, isLoggedIn, lang, reg, env,LayoutModel?.IsLoggedInUserSecured, LayoutModel?.SecuredCaseStatus);
      getGTMVariablesPageView(
        window.document.title,
        window.location.href,
        window.location.pathname,
        userid,
        isLoggedIn,
        lang,
        reg,
        env,
      );
    }
  }, [LayoutModel]);

  return (
    <div className={styles.wrapper}>
      <div className="sectionGutterV Section--secondaryBg u-paddingTz">
        <main className="CreateRepaymentPlan upaddingTz">
          {Model.IsMainDetailsSectionVisible &&
            Model.IsPaymentPlanFinalizingError && (
              <h3
                id="payment-plan-payment-finalizing-error"
                className="u-textCenter u-marginBxxsm u-block u-colorTextRed"
              >
                <>{PageData?.PaymentPlanFinalizingError}</>
              </h3>
            )}
          {Model.IsMainDetailsSectionVisible &&
            Model.IsBlockWithExternalPaymentReferenceVisible && (
              <p
                id="payment-plan-external-payment-reference"
                className="u-textCenter"
              >
                <>
                  {PageData?.ExternalPaymentReference}:
                  {Model.ExternalPaymentReference}
                </>
              </p>
            )}

          {Model.IsMainDetailsSectionVisible &&
            Model.IsPaymentSummaryVisible && (
              <PaymentSummaryDetails {...props} />
            )}
          {Model.IsAutoRetryPlanCreation && <ContentLoader />}
          {!Model.IsAutoRetryPlanCreation && (
            <section className="CreateRepaymentPlan__section">
              <div className="CreateRepaymentPlan__ctaWrapper CreateRepaymentPlan__ctaWrapper--centerAlign">
                <PageLink
                  url={Model.ClosePageUrl}
                  text={PageData.CloseButtonLabel}
                  className="Button CreateRepaymentPlan__cancelBtn"
                />
              </div>
            </section>
          )}
        </main>
        <div
          className={classNames(
            'modal-overlay',
            showPaymentFailedPopup ? 'is-active' : '',
          )}
        >
          <PaymentFailedPopup
            CallUsNowButtonText={PageData?.CallUsNowButtonText}
            PaymentErrorHeader={PageData?.PaymentErrorHeader}
            PaymentErrorInfo={PageData?.PaymentErrorInfo}
            PaymentErrorSecondAttemptInfo={
              PageData?.PaymentErrorInfoSecondAttemptContent
            }
            TryAgainButtonText={PageData?.TryAgainButtonText}
            SendMessageButtonText={PageData?.SendMessageButtonText}
            handleClick={() => {
              setShowPaymentFailedPopup(false);
            }}
            ShowSecondAttempt={showPaymentSecondAttempt}
            PlanCreationFlow={true}
            RequestCallBackButtonText={PageData?.RequestCallBackButtonText}
            {...props}
          />
        </div>
        <div
          className={classNames(
            'modal-overlay',
            showSurveyPopup ? 'is-active' : '',
          )}
        >
          <div className="modal-dialog iframe_pop">
            <SurveyResultPopup closePop={setShowSurveyPopup} source={Model?.NetigateSurveyUrl}></SurveyResultPopup>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PaymentPlanSummaryPageData;
